import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench,
  Cog,
  Droplet,
  Gauge,
  Hammer,
  ShoppingBag,
  Factory,
  MapPin,
  Settings,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TestBench from '../assets/Images/infra/img3.jpg';

import cylinder from '../assets/Images/services/cylinder.jpg'
import pumps from '../assets/Images/services/pumpsRecondition.avif'
import motor from '../assets/Images/services/PumpsAndMotors.webp'
import Onsite from '../assets/Images/services/onsiteService.avif'
import rocks from '../assets/Images/services/excavatorBreakers.webp'

import gearbox from '../assets/Images/services/hydraulicGearbox.jpg'
import control from '../assets/Images/services/controlV.avif'
import circuit from '../assets/Images/services/circuitBlocks.webp'
import oepart from '../assets/Images/services/oeParts.webp'
import precision from '../assets/Images/services/Precison.jpeg'

const Services = () => {
  const navigate = useNavigate();
  const servicesRef = useRef([]);

  // Handle hash-based scrolling when the component mounts
  useEffect(() => {
    const scrollToHash = () => {
      const { hash } = window.location;
      if (hash) {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    // Initial scroll to hash
    scrollToHash();

    // Also handle hash changes
    const handleHashChange = () => {
      scrollToHash();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target;
            const isEven = card.getAttribute('data-index') % 2 === 0;

            // Add animation class based on position
            card.classList.add(isEven ? 'animate-fadeInLeft' : 'animate-fadeInRight');
            card.style.opacity = '1';

            // Lazy load image if not already loaded
            const img = card.querySelector('img[data-src]');
            if (img) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }

            // Animate key points with staggered delay
            const keyPoints = card.querySelectorAll('.key-point-item');
            keyPoints.forEach((point, idx) => {
              setTimeout(() => {
                point.style.opacity = '1';
                point.style.transform = 'translateX(0)';
              }, idx * 100);
            });

            // Unobserve after animation
            observer.unobserve(card);
          }
        });
      },
      {
        rootMargin: '100px 0px',
        threshold: 0.1
      }
    );

    // Observe all service cards
    const currentRefs = servicesRef.current;
    currentRefs.forEach((card) => card && observer.observe(card));

    return () => {
      currentRefs.forEach((card) => card && observer.unobserve(card));
    };
  }, []);



  const keyFeaturedServices = [
    {
      icon: <Wrench size={40} />,
      title: 'Hydraulic Cylinder Refurbish & Manufacturing',
      slug: 'hydraulic-cylinder-refurbishment',
      description:
        'Complete repair and custom manufacturing of single- and double-acting hydraulic cylinders for heavy-duty machines like excavators, loaders, and rock breakers. Each cylinder is inspected, machined, and pressure-tested to meet OEM standards.',
      image: cylinder,
      benefits: [
        'Restore damaged cylinders to OEM performance',
        'Custom-build cylinders as per design requirements',
        'Ensure leak-free sealing and smooth operation',
        'Enhance system efficiency and durability'
      ]
    },
    {
      icon: <Droplet size={40} />,
      title: 'Hydraulic Pump Reconditioning & Supply',
      slug: 'hydraulic-pump-reconditioning',
      description:
        'Reconditioning, repair, and supply of piston, gear, and vane hydraulic pumps with in-house testing to ensure factory-level performance and reliability.',
      image: pumps,
      benefits: [
        'Restore pumps to full performance',
        'Reduce downtime with sustainable repair',
        'Provide OEM/equivalent replacement pumps',
        'Maintain precise flow and pressure balance'
      ]
    },
    {
      icon: <Cog size={40} />,
      title: 'Hydraulic Motors Refurbish & Supply',
      slug: 'hydraulic-motors-refurbish',
      description:
        'Refurbishing all types of hydraulic motors—orbital, gear, piston, and vane—to ensure maximum torque, speed, and reliability for all heavy machinery.',
      image: motor,
      benefits: [
        'Refurbish and supply reliable motors',
        'Eliminate vibration and overheating issues',
        'Optimize motor efficiency and power',
        'Extend operational lifespan'
      ]
    },
    {
      icon: <MapPin size={40} />,
      title: 'On-site Hydraulic Services',
      slug: 'onsite-hydraulic-services',
      description:
        'On-site hydraulic maintenance and repair for excavators, crushers, and loaders, minimizing downtime with quick and professional field service.',
      image: Onsite,
      benefits: [
        'Rapid response and field repairs',
        'Hydraulic leak detection and testing',
        '24/7 emergency support',
        'Minimize downtime and cost'
      ]
    },
    {
      icon: <Hammer size={40} />,
      title: 'Rock Breaker Refurbishment',
      slug: 'rock-breaker-refurbishment',
      description:
        'Comprehensive overhaul of rock breakers using genuine spares to restore impact power, reliability, and performance across all major brands.',
      image: rocks,
      benefits: [
        'Restore striking efficiency and impact force',
        'Use genuine parts for durability',
        'Tested for hydraulic and nitrogen balance',
        'Ensure leak-free, reliable operation'
      ]
    }
  ];


  // Other Services
  const otherServices = [
    {
      icon: <Settings size={40} />,
      title: 'Hydraulic Gear Box Refurbish & Supply',
      slug: 'hydraulic-gearbox-refurbish-supply',
      description:
        'Complete overhaul, repair, and supply of hydraulic gearboxes for excavators, crushers, and screening plants, ensuring smooth power transmission and reliability.',
      image: gearbox,
      benefits: [
        'Restore gearbox efficiency and stability',
        'Prevent gear wear and overheating',
        'Provide OEM or equivalent gearboxes',
        'Ensure smooth and quiet operation'
      ]
    },
    {
      icon: <Gauge size={40} />,
      title: 'Hydraulic control Valves ',
      slug: 'hydraulic-valves-refurbish-supply',
      description:
        'Diagnosis, repair, and supply of hydraulic control, relief, and directional valves to restore precise flow regulation and system responsiveness.',
      image: control,
      benefits: [
        'Restore valves to OEM standards',
        'Eliminate leakage and inefficiency',
        'Ensure accurate pressure control and flow',
        'Supply OEM/equivalent valves and manifolds'
      ]
    },
    {
      icon: <Wrench size={40} />,
      title: 'Hydraulic Circuit Blocks Refurbish & Supply',
      slug: 'hydraulic-circuit-blocks-refurbish-supply',
      description:
        'Refurbishment and calibration of hydraulic circuit blocks and manifolds to ensure correct flow, leak-free operation, and performance consistency.',
      image: circuit,
      benefits: [
        'Restore internal flow control ',
        'Ensure calibrated and leak-free ',
        'Provide compatible refurbished blocks',
        'Improve flow and pressure distribution'
      ]
    },
    {
      icon: <Gauge size={40} />,
      title: 'Test Benches for Hydraulic Diagnostics',
      slug: 'hydraulic-test-benches',
      description:
        'Advanced in-house test benches simulate real hydraulic conditions to test pumps, motors, and cylinders for flow rate, pressure, and efficiency.',
      image: TestBench,
      benefits: [
        'Precision component testing and validation',
        'Detect wear or efficiency loss early',
        'Certify OEM-level performance',
        'Provide transparent test reports'
      ]
    },
    {
      icon: <ShoppingBag size={40} />,
      title: 'OE & Equivalent Parts Sales',
      slug: 'oe-equivalent-parts-sales',
      description:
        'Supply of high-quality OEM and equivalent parts including pumps, valves, seal kits, hoses, and undercarriage components for all major brands.',
      image: oepart,
      benefits: [
        'Genuine and equivalent spare parts',
        'Quick availability and delivery',
        'Fitment-tested for reliability',
        'Warranty-backed components'
      ]
    },
    {
      icon: <Factory size={40} />,
      title: 'Precision Machine Shop',
      slug: 'precision-machine-shop',
      description:
        'Fully equipped in-house machine shop offering CNC machining, welding, honing, and assembly for precise hydraulic component fabrication and repair.',
      image: precision,
      benefits: [
        'High-accuracy CNC machining',
        'Custom fabrication and repair',
        'Strict quality inspection',
        'Fast turnaround with full in-house control'
      ]
    }
  ];

  // Function to render service cards
  const renderServiceCard = (service, index, isFeatured = false) => {
    // For featured services, use the new layout with alternating images
    if (isFeatured) {
      const isEven = index % 2 === 0;
      const imageSection = (
        <div className={`relative ${isEven ? 'md:order-1' : 'md:order-2'} h-64 md:h-auto`}>
          <div className="w-full overflow-hidden">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-auto max-h-[500px] object-cover rounded-t-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIiBmaWxsPSIjZWVlZWVlIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCxzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iYmxhY2siPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
        </div>
      );

      const contentSection = (
        <div className={`p-8 flex flex-col justify-center ${isEven ? 'md:order-2' : 'md:order-1'}`}>
          <div className="flex items-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full mr-4">
              <div className="text-blue-600">
                {React.cloneElement(service.icon, { size: 24 })}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-[#00325b]">{service.title}</h3>
          </div>

          <p className="text-gray-600 mb-6">{service.description}</p>

          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Key Benefits:</h4>
            <ul className="space-y-3">
              {service.benefits.map((benefit, idx) => (
                <li
                  key={idx}
                  className="key-point-item flex items-start opacity-0 transition-all duration-300 ease-out"
                  style={{
                    transform: 'translateX(20px)',
                    transitionDelay: `${idx * 50}ms`
                  }}
                >
                  <svg
                    className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <button
            className="self-start bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-300"
            onClick={() => navigate(`/services/${service.slug}`)}
          >
            Learn More
          </button>
        </div>
      );

      return (
        <div
          key={index}
          ref={(el) => (servicesRef.current[index] = el)}
          data-index={index}
          className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 opacity-0 ${isFeatured ? 'border-2 border-blue-100' : ''
            }`}
        >
          <div className="md:flex">
            {isEven ? (
              <>
                <div className="md:w-1/2">
                  {imageSection}
                </div>
                <div className="md:w-1/2">
                  {contentSection}
                </div>
              </>
            ) : (
              <>
                <div className="md:w-1/2 md:order-2">
                  {imageSection}
                </div>
                <div className="md:w-1/2 md:order-1">
                  {contentSection}
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Original card layout for non-featured services
    return (
      <div
        key={index}
        ref={(el) => (servicesRef.current[index] = el)}
        data-index={index}
        className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 flex flex-col h-full opacity-0 ${isFeatured ? 'border-2 border-blue-100' : ''
          }`}
      >
        <div className="relative h-64 overflow-hidden">
          <div className="w-full overflow-hidden">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-auto max-h-[500px] object-contain rounded-t-lg hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIiBmaWxsPSIjZWVlZWVlIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCxzYW5zLXNlcmlmIiBmb250LXNpemU9IjEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iYmxhY2siPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
              }}
            />
          </div>
          <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full shadow-md">
            <div className="text-blue-600">
              {React.cloneElement(service.icon, { size: 24 })}
            </div>
          </div>
        </div>
        <div className="p-6 flex flex-col flex-grow">
          <h3 className="text-xl font-bold text-[#00325b] mb-3">{service.title}</h3>
          <p className="text-gray-600 mb-4 flex-grow">{service.description}</p>

          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Key Benefits:</h4>
            <ul className="space-y-2">
              {service.benefits.map((benefit, idx) => (
                <li
                  key={idx}
                  className="key-point-item flex items-start opacity-0 transition-all duration-300 ease-out"
                  style={{
                    transform: 'translateX(20px)',
                    transitionDelay: `${idx * 50}ms`
                  }}
                >
                  <svg
                    className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>


        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <section className="page-header bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fadeInDown">
            Our Services
          </h1>
          <p className="text-xl text-blue-100 animate-fadeInDown delay-200">
            Comprehensive hydraulic and equipment solutions for maximum uptime
          </p>
        </div>
      </section>

      {/* Services Intro */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#00325b] mb-6">
              Complete Equipment Service Solutions
            </h2>
            <p className="text-gray-600 text-lg">
              From routine maintenance to emergency repairs, our comprehensive range of services is
              designed to keep your heavy equipment operating at peak efficiency. Our experienced
              technicians and state-of-the-art facilities enable us to handle any challenge.
            </p>
          </div>
        </div>
      </section>

      {/* Key Featured Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#00325b]">
            Key Featured Services
          </h2>
          <div className="grid grid-cols-1 gap-8">
            {keyFeaturedServices.map((service, index) => (
              renderServiceCard(service, index, true)
            ))}
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section id="other-services" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-[#00325b]">
            Other Services
          </h2>
          <div className="mx-4 md:mx-8 lg:mx-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherServices.map((service, index) => (
                renderServiceCard(service, index + keyFeaturedServices.length, false)
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Experience Premium Hydraulic Solutions?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Our team of experts is ready to provide top-notch hydraulic services tailored to your specific needs. Get in touch today for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => navigate('/contact')}
                className="bg-white text-blue-900 hover:bg-blue-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-300"
              >
                Get a Free Quote
              </button>
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold py-3 px-8 rounded-lg transition-colors duration-300"
              >
                Contact Our Experts
              </button>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;